import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, HelpCircle, Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTriageItem } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";
import { AskQuestionSheet } from "./ask-question-sheet";
import { DecisionWizard } from "./decision-wizard";
import { LinkedQuestionsSheet } from "./linked-questions-sheet";
import { MatchedStorySheet } from "./matched-story-sheet";
import { NotesThread } from "./notes-thread";
import { TranscriptContextSheet } from "./transcript-context-sheet";
import { ConfidenceDots, StatusBadge } from "./triage-badge";
import { TYPES } from "./triage-types";

interface ReviewDetailProps {
  itemId: string;
  prevItemId: string | null;
  nextItemId: string | null;
  collapsed?: boolean;
}

export function ReviewDetail({
  itemId,
  prevItemId,
  nextItemId,
  collapsed = false,
}: ReviewDetailProps) {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const { data, isLoading, isError } = useTriageItem(itemId);
  const [askOpen, setAskOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [linkedQuestionsOpen, setLinkedQuestionsOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full mt-4" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="p-6 text-sm text-destructive">Failed to load item.</div>;
  }

  const typeLabel = TYPES[data.type].label;

  return (
    <div className="flex flex-col h-full">
      <div className={cn("flex-1 overflow-y-auto", collapsed ? "px-12 py-10" : "px-6 py-6")}>
        <article className={cn("space-y-7 mx-auto", collapsed ? "max-w-[760px]" : "max-w-none")}>
          {/* Header */}
          <header>
            <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              <span className="font-mono tabular-nums text-foreground/80">
                {data.externalKey ?? data.id.slice(0, 6)}
              </span>
              <span aria-hidden>·</span>
              <span>{typeLabel}</span>
              {data.linkedQuestionIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setLinkedQuestionsOpen(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-warning-bg text-warning border border-warning/30 px-2 py-0.5 text-[10px] normal-case tracking-normal font-semibold hover:opacity-90"
                  title="View linked questions"
                >
                  <HelpCircle className="h-3 w-3" />
                  {data.linkedQuestionIds.length} question
                  {data.linkedQuestionIds.length === 1 ? "" : "s"}
                </button>
              )}
              <span className="ml-auto inline-flex items-center gap-2">
                <ConfidenceDots confidence={data.confidence} />
                <StatusBadge status={data.decision ? data.effectiveStatus : "pending"} />
              </span>
            </div>
            <h2 className="font-display text-2xl font-bold leading-tight tracking-tight text-foreground">
              {data.extracted}
            </h2>
          </header>

          {/* EVIDENCE */}
          <section>
            <SectionLabel>Evidence</SectionLabel>
            <blockquote className="text-[15px] italic text-foreground/85 leading-relaxed">
              "{data.quote}"
            </blockquote>
            {data.quoteContext && (
              <p className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">
                {data.quoteContext}
              </p>
            )}
            {data.speaker && data.source && (
              <div className="mt-4 rounded-lg border border-[var(--color-border)] p-3 flex items-center gap-3">
                <span
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold shrink-0",
                    data.speaker.org === "client"
                      ? "bg-nymbl-sky text-nymbl-navy"
                      : "bg-nymbl-navy text-white",
                  )}
                >
                  {data.speaker.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-sm leading-tight">
                    {data.speaker.name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{data.speaker.role}</div>
                  <div className="text-[11px] text-muted-foreground/80 truncate">
                    {data.source.name} · {new Date(data.sourceDate).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTranscriptOpen(true)}
                  className="text-xs text-nymbl-blue hover:underline shrink-0"
                  title="See the quote in transcript context"
                >
                  Transcript
                </button>
              </div>
            )}
          </section>

          {/* AI SUGGESTION */}
          {data.mapStory && (
            <section>
              <SectionLabel icon={<Sparkles className="h-3 w-3" />}>AI suggestion</SectionLabel>
              <button
                type="button"
                onClick={() => setMatchOpen(true)}
                className="w-full text-left block rounded-lg bg-nymbl-sky/60 border border-nymbl-sky px-4 py-3 text-sm leading-relaxed hover:bg-nymbl-sky transition-colors cursor-pointer"
                title="Open the matched story in a slide-out — the queue stays in place."
              >
                This matches{" "}
                <span className="font-mono text-[13px] font-semibold">{data.mapStory.code}</span>{" "}
                <span className="italic">{data.mapStory.label?.trim() || data.mapStory.text}</span>
                {data.overlapPct > 0 && (
                  <>
                    {" with "}
                    <span className="font-semibold">{data.overlapPct}%</span> overlap
                    {data.overlapPct >= 80
                      ? " — strong match."
                      : data.overlapPct >= 50
                        ? " — partial match."
                        : " — weak match."}
                  </>
                )}
              </button>
            </section>
          )}

          {/* DECISION */}
          <section>
            <div className="flex items-center justify-between mb-2.5">
              <SectionLabel className="mb-0">Decision</SectionLabel>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAskOpen(true)}
                className="h-7 text-xs"
              >
                <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
                Ask a question
              </Button>
            </div>
            <DecisionWizard
              itemId={data.id}
              decision={data.decision}
              suggestedStatus={data.suggestedStatus}
              linkedQuestionCount={data.linkedQuestionIds.length}
            />
          </section>

          {/* NOTES */}
          <section>
            <SectionLabel>Notes</SectionLabel>
            <NotesThread itemId={data.id} notes={data.notes} />
          </section>
        </article>
      </div>

      <AskQuestionSheet
        open={askOpen}
        onOpenChange={setAskOpen}
        solutionId={solutionId}
        itemId={data.id}
        itemExternalKey={data.externalKey}
        itemSummary={data.extracted}
        itemSourceId={data.sourceId}
      />

      <MatchedStorySheet
        open={matchOpen}
        onOpenChange={setMatchOpen}
        storyId={data.mapStory?.id ?? null}
        solutionId={solutionId}
        highlightItemId={data.id}
      />

      <TranscriptContextSheet
        open={transcriptOpen}
        onOpenChange={setTranscriptOpen}
        sourceId={data.sourceId}
        quote={data.quote}
        quoteContext={data.quoteContext}
      />

      <LinkedQuestionsSheet
        open={linkedQuestionsOpen}
        onOpenChange={setLinkedQuestionsOpen}
        solutionId={solutionId}
        questionIds={data.linkedQuestionIds}
      />

      <footer className="border-t border-[var(--color-border)] px-6 py-3 flex items-center justify-between shrink-0">
        <Button
          variant="outline"
          size="sm"
          asChild
          disabled={prevItemId == null}
          className={cn(prevItemId == null && "pointer-events-none opacity-40")}
          title="Previous (k or ↑)"
        >
          {prevItemId ? (
            <Link to="/s/$solutionId/review/$itemId" params={{ solutionId, itemId: prevItemId }}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />
              Prev
            </Link>
          ) : (
            <span>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />
              Prev
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          asChild
          disabled={nextItemId == null}
          className={cn(nextItemId == null && "pointer-events-none opacity-40")}
          title="Next (j or ↓)"
        >
          {nextItemId ? (
            <Link to="/s/$solutionId/review/$itemId" params={{ solutionId, itemId: nextItemId }}>
              Next
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          ) : (
            <span>
              Next
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </span>
          )}
        </Button>
      </footer>
    </div>
  );
}

function SectionLabel({
  children,
  icon,
  className,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={cn(
        "text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2.5 inline-flex items-center gap-1.5",
        className,
      )}
    >
      {icon}
      {children}
    </h3>
  );
}
